import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ArrowLeft, Star, Info, ExternalLink, AlertTriangle, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { fetchGalleryItemById, GalleryItem } from '@/services/galleryService';
import { useToast } from '@/hooks/use-toast';
import AdminPanel from '@/components/AdminPanel';

const GalleryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coverDetails, setCoverDetails] = useState<GalleryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadGalleryItem = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const data = await fetchGalleryItemById(id);
        if (data) {
          setCoverDetails(data);
          console.log("Image location:", data.image_url);
        } else {
          setError("Cover not found");
        }
      } catch (err) {
        console.error('Failed to fetch gallery item:', err);
        setError('Failed to load cover details. Please try again later.');
        toast({
          title: "Error Loading Cover",
          description: "Could not load cover details. Please try refreshing.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadGalleryItem();
  }, [id, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="container mx-auto px-4 py-8 flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-bookNavy" />
            <h2 className="text-xl font-medium">Loading cover details...</h2>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !coverDetails) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="container mx-auto px-4 py-8 flex-grow flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Cover not found</h2>
            <p className="mb-6 text-gray-600">{error}</p>
            <Button onClick={() => navigate('/gallery')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Gallery
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 7) return 'text-green-500';
    if (rating >= 4) return 'text-amber-500';
    return 'text-red-500';
  };

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[...Array(10)].map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating ? 'text-bookGold fill-bookGold' : 'text-gray-300'
            }`}
          />
        ))}
        <span className={`ml-2 font-medium ${getRatingColor(rating)}`}>
          {rating}/10
        </span>
      </div>
    );
  };

  const getFilename = (path: string) => {
    return path.split('/').pop();
  };

  const openImageInNewTab = () => {
    window.open(coverDetails.image_url, '_blank');
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleItemDeleted = () => {
    toast({
      title: "Item Deleted",
      description: "The gallery item has been removed",
    });
    navigate('/gallery');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <Button 
          variant="outline" 
          onClick={() => navigate('/gallery')}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Gallery
        </Button>
        
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <div className="sticky top-8">
                <Card className="overflow-hidden shadow-lg">
                  <AspectRatio ratio={2/3}>
                    {imageError ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 p-4 text-center">
                        <div>
                          <AlertTriangle className="h-12 w-12 mx-auto mb-2 text-amber-500" />
                          <p>Image not available</p>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={coverDetails.image_url} 
                        alt={`${coverDetails.title} by ${coverDetails.author}`}
                        className="object-cover w-full h-full"
                        onError={handleImageError}
                      />
                    )}
                    <div className={`absolute top-2 right-2 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                      coverDetails.rating >= 7 ? 'bg-green-500' : coverDetails.rating <= 3 ? 'bg-red-500' : 'bg-amber-500'
                    }`}>
                      {coverDetails.rating}
                    </div>
                  </AspectRatio>
                </Card>
                
                <div className="mt-4 bg-gray-50 p-3 rounded text-sm flex items-start">
                  <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium mb-1">Image Location</p>
                    <p className="text-gray-600 break-all">
                      <span>Image filename: <code className="bg-gray-100 px-1 rounded">{getFilename(coverDetails.image_url)}</code></span>
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 h-8 text-xs"
                      onClick={openImageInNewTab}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Open Image
                    </Button>
                  </div>
                </div>
                
                <AdminPanel 
                  itemId={coverDetails.id} 
                  onDelete={handleItemDeleted} 
                />
              </div>
            </div>
            
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">{coverDetails.title}</CardTitle>
                  <p className="text-gray-500">by {coverDetails.author}</p>
                  <div className="mt-4">
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                      {coverDetails.genre}
                    </span>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-2">Overall Rating</h3>
                    {renderRatingStars(coverDetails.rating)}
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="font-medium mb-2">Critique</h3>
                    <p className="text-gray-700 whitespace-pre-line">{coverDetails.full_critique || coverDetails.summary}</p>
                  </div>
                  
                  {coverDetails.suggestions && coverDetails.suggestions.length > 0 && (
                    <div>
                      <h3 className="font-medium mb-2">Suggestions</h3>
                      <ul className="list-disc pl-5 space-y-2">
                        {coverDetails.suggestions.map((suggestion: string, index: number) => (
                          <li key={index} className="text-gray-700">{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-500 pt-4">
                    <p>Submitted on {new Date(coverDetails.submitted_date).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default GalleryDetailPage;
